import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from 'axios';
import { protectedApi } from "../../app/utils/common/protectedApi";
import { APIS } from "../../app/utils/constants/apiConstant";

const buildLeaveListQuery = ({ page, limit = 10, status, search, month }) => {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });
  if (status) params.set("status", status);
  if (search) params.set("search", String(search).trim());
  if (month) params.set("month", String(month).trim());
  return params.toString();
};

export const getUserLeaveRequestAPI = createAsyncThunk(
  "leave/getUserLeaveRequestAPI",
  async ({ page, limit = 10, status }, { rejectWithValue }) => {
    try {
      const response = await protectedApi({
        url: `${APIS.GET_USER_LEAVES}?${buildLeaveListQuery({ page, limit, status })}`,
        method: "GET",

      });

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data.error);
    }
  })
export const createLeaveRequestAPI = createAsyncThunk(
  "leave/createLeaveRequestAPI",
  async (data, { rejectWithValue }) => {
    try {
      const response = await protectedApi({
        url: `${APIS.CREATE_LEAVE_REQUEST}`,
        method: "POST",
        data

      });

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data.error);
    }
  });

/** HR/Admin: assign approved WFH to an employee. */
export const createWfhForEmployeeAPI = createAsyncThunk(
  "leave/createWfhForEmployeeAPI",
  async (data, { rejectWithValue }) => {
    try {
      const response = await protectedApi({
        url: APIS.CREATE_WFH_FOR_EMPLOYEE,
        method: "POST",
        data,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error);
    }
  });
export const getAllLeavesAPI = createAsyncThunk(
  "leave/getAllLeavesAPI",
  async ({ page, limit = 10, status, search, month }, { rejectWithValue }) => {
    try {
      const response = await protectedApi({
        url: `${APIS.GET_ALL_LEAVES}?${buildLeaveListQuery({ page, limit, status, search, month })}`,
        method: "GET",

      });

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data.error);
    }
  })

export const cancelPendingLeaveRequestAPI = createAsyncThunk(
  "leave/cancelPendingLeaveRequestAPI",
  async ({ leaveRequestId, comment }, { rejectWithValue }) => {
    const urls = [APIS.CANCEL_PENDING_LEAVE_REQUEST, APIS.CANCEL_PENDING_LEAVE_REQUEST_ALT];
    let lastErr;
    for (const url of urls) {
      try {
        const response = await protectedApi({
          url,
          method: "POST",
          data: { leaveRequestId, ...(comment !== undefined ? { comment } : {}) },
        });
        return response.data;
      } catch (error) {
        lastErr = error;
        const status = error?.response?.status;
        const msg = String(error?.response?.data?.error?.message || "").toLowerCase();
        const isPathNotFound = msg.includes("path not found");
        if ((status === 404 || isPathNotFound) && url !== urls[urls.length - 1]) {
          continue;
        }
        return rejectWithValue(error.response?.data?.error);
      }
    }
    return rejectWithValue(lastErr?.response?.data?.error);
  }
);

export const updateOwnLeaveRequestAPI = createAsyncThunk(
  "leave/updateOwnLeaveRequestAPI",
  async (data, { rejectWithValue }) => {
    const urls = [APIS.UPDATE_OWN_LEAVE_REQUEST, APIS.UPDATE_OWN_LEAVE_REQUEST_ALT];
    let lastErr;
    for (const url of urls) {
      try {
        const response = await protectedApi({
          url,
          method: "POST",
          data,
        });
        return response.data;
      } catch (error) {
        lastErr = error;
        const status = error?.response?.status;
        if (status === 404 && url !== urls[urls.length - 1]) {
          continue;
        }
        return rejectWithValue(error.response?.data?.error);
      }
    }
    return rejectWithValue(lastErr?.response?.data?.error);
  }
);


/** Employee: submit cancellation of approved leave for HR review. */
export const requestCancelApprovedLeaveAPI = createAsyncThunk(
  "leave/requestCancelApprovedLeaveAPI",
  async ({ leaveRequestId, comment }, { rejectWithValue }) => {
    try {
      const response = await protectedApi({
        url: APIS.CANCEL_APPROVED_LEAVE,
        method: "POST",
        data: { leaveRequestId, comment },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error);
    }
  }
);

/** HR/Admin: approve or reject pending cancellation of approved leave. */
export const reviewCancelApprovedLeaveAPI = createAsyncThunk(
  "leave/reviewCancelApprovedLeaveAPI",
  async ({ leaveRequestId, decision, comment }, { rejectWithValue }) => {
    try {
      const response = await protectedApi({
        url: APIS.REVIEW_CANCEL_APPROVED_LEAVE,
        method: "POST",
        data: {
          leaveRequestId,
          decision,
          ...(comment !== undefined && comment !== "" ? { comment } : {}),
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error);
    }
  }
);

/** HR/Admin: immediately cancel approved leave (no employee request required). */
export const revokeApprovedLeaveAPI = createAsyncThunk(
  "leave/revokeApprovedLeaveAPI",
  async ({ leaveRequestId, comment }, { rejectWithValue }) => {
    try {
      const response = await protectedApi({
        url: APIS.REVOKE_APPROVED_LEAVE,
        method: "POST",
        data: { leaveRequestId, comment },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error);
    }
  }
);

export const addLeaveCommentAPI = createAsyncThunk(
  "leave/addLeaveCommentAPI",
  async (data, { rejectWithValue }) => {
    try {
      const response = await protectedApi({
        url: `${APIS.ADD_LEAVE_COMMENT}`,
        method: "POST",
        data
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data.error);
    }
  })

export const addAdminHrLeaveNoteAPI = createAsyncThunk(
  "leave/addAdminHrLeaveNoteAPI",
  async ({ leaveRequestId, comment }, { rejectWithValue }) => {
    try {
      const response = await protectedApi({
        url: APIS.ADD_ADMIN_HR_LEAVE_NOTE,
        method: "POST",
        data: { leaveRequestId, comment },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error);
    }
  }
);
export const sendEmailForLeaveRequestAPI = createAsyncThunk(
  "leave/sendEmailForLeaveRequestAPI",
  async (data, { rejectWithValue }) => {
    try {
      const response = await protectedApi({
        url: `${APIS.SEND_EMAIL_FOR_LEAVE_REQUEST}`,
        method: "POST",
        data
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data.error);
    }
  })

export const getLeaveNotificationConfigAPI = async () => {
  const response = await protectedApi({
    url: APIS.GET_LEAVE_NOTIFICATION_CONFIG,
    method: "GET",
  });
  return response.data;
};

export const updateLeaveNotificationConfigAPI = async (data) => {
  const response = await protectedApi({
    url: APIS.UPDATE_LEAVE_NOTIFICATION_CONFIG,
    method: "PUT",
    data,
  });
  return response.data;
};

export const sendLeaveNotificationTestAPI = async (data = {}) => {
  const response = await protectedApi({
    url: APIS.SEND_LEAVE_NOTIFICATION_TEST,
    method: "POST",
    data,
  });
  return response.data;
};

export const getMyLeaveBalanceAPI = async () => {
  const response = await protectedApi({
    url: APIS.LEAVE_BALANCE_ME,
    method: "GET",
  });
  return response.data;
};

/** Leave days (weekends/holidays excluded) and paid/unpaid split for a draft request. */
export const getLeaveRequestPreviewAPI = async ({ leaveType, startDate, endDate, startTime, endTime }) => {
  const params = new URLSearchParams({ startDate, endDate: endDate || startDate });
  if (leaveType) params.set("leaveType", leaveType);
  if (startTime) params.set("startTime", startTime);
  if (endTime) params.set("endTime", endTime);
  const response = await protectedApi({
    url: `${APIS.LEAVE_REQUEST_PREVIEW}?${params.toString()}`,
    method: "GET",
  });
  return response.data;
};

export const getAdminLeaveBalancesAPI = async () => {
  const response = await protectedApi({
    url: APIS.LEAVE_BALANCE_ADMIN,
    method: "GET",
  });
  return response.data;
};

export const getAdminExEmployeeLeaveBalancesAPI = async () => {
  const response = await protectedApi({
    url: APIS.LEAVE_BALANCE_ADMIN_EX_EMPLOYEES,
    method: "GET",
  });
  return response.data;
};

export const updateAdminLeaveBalanceAPI = async (data) => {
  const response = await protectedApi({
    url: APIS.LEAVE_BALANCE_ADMIN,
    method: "PUT",
    data,
  });
  return response.data;
};

